
// src/App.js

import React, { useEffect, useState } from "react";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

// =====================================================
// COMPONENT IMPORTS
// =====================================================
// FirestoreData.js path:
// E:\home-service-admin\home-service-admin\src\components\FirestoreData.js
//
// Login.js should be located at:
// E:\home-service-admin\home-service-admin\src\components\Login.js
// =====================================================
import FirestoreData from "./components/FirestoreData";
import Login from "./components/Login";

// =====================================================
// CSS / OTHER IMPORTS
// =====================================================
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// App.css path:
// E:\home-service-admin\home-service-admin\src\App.css
import "./App.css";

// =====================================================
// Firebase Configuration
// =====================================================
const firebaseConfig = {
  apiKey: "AIzaSyBuGlLEn5AK95FlHX9OAW5c7DAwXwpBMMQ",
  authDomain: "urbanclapclone-8dc68.firebaseapp.com",
  projectId: "urbanclapclone-8dc68",
  storageBucket: "urbanclapclone-8dc68.firebasestorage.app",
  messagingSenderId: "891430644958",
  appId: "1:891430644958:web:4be7e03968b497c8ace64f",
  measurementId: "G-TVPCNE1P0V",
};

// =====================================================
// Initialize Firebase
// =====================================================
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// =====================================================
// FCM VAPID Key
// =====================================================
const FCM_VAPID_KEY =
  "BGJWyVrh6Zgxb3fkYOQztkajgIJpL595pMl9y08ikhx4YO-HL47xcjcUNbdamgBX-D23OzxoYt0GjKUWrzFFoTw";

// =====================================================
// React Application
// =====================================================
function App() {
  const [currentUser, setCurrentUser] = useState(null);

  // ===================================================
  // Login Success Handler
  // ===================================================
  const handleLoginSuccess = async (user) => {
    try {
      console.log("✅ Logged in:", user);

      setCurrentUser(user);

      if (!user?.uid) {
        console.warn(
          "⚠️ FCM skipped: Firebase Authentication UID is missing."
        );
        return;
      }

      // Wait until Firebase Auth confirms the session
      const unsubscribe = onAuthStateChanged(
        auth,
        async (firebaseUser) => {
          if (firebaseUser && firebaseUser.uid === user.uid) {
            unsubscribe();

            // Force-refresh the ID token
            try {
              await firebaseUser.getIdToken(true);
            } catch (tokenError) {
              console.warn(
                "⚠️ Failed to refresh ID token before FCM setup:",
                tokenError
              );
            }

            await initializeFCM(firebaseUser.uid);
          }
        }
      );
    } catch (error) {
      console.error(
        "❌ Login success handler error:",
        error
      );
    }
  };

  // ===================================================
  // Initialize Firebase Cloud Messaging
  // ===================================================
  const initializeFCM = async (userId) => {
    try {
      console.log("======================================");
      console.log("🔄 Starting FCM setup...");
      console.log("👤 Firebase User UID:", userId);
      console.log("======================================");

      // -------------------------------------------------
      // Validate User ID
      // -------------------------------------------------
      if (!userId) {
        console.warn(
          "⚠️ FCM setup stopped: User ID is missing."
        );
        return;
      }

      // -------------------------------------------------
      // Confirm authenticated user
      // -------------------------------------------------
      if (
        !auth.currentUser ||
        auth.currentUser.uid !== userId
      ) {
        console.warn(
          "⚠️ FCM setup stopped: No matching authenticated user found.",
          {
            expected: userId,
            actual: auth.currentUser?.uid,
          }
        );
        return;
      }

      // -------------------------------------------------
      // Check Browser Support
      // -------------------------------------------------
      if (typeof window === "undefined") {
        console.warn(
          "⚠️ FCM setup skipped: Window is unavailable."
        );
        return;
      }

      if (!("Notification" in window)) {
        console.warn(
          "⚠️ Browser notifications are not supported."
        );

        toast.warning(
          "Push notifications are not supported in this browser."
        );

        return;
      }

      if (!("serviceWorker" in navigator)) {
        console.warn(
          "⚠️ Service Workers are not supported."
        );

        toast.warning(
          "This browser does not support service workers."
        );

        return;
      }

      // -------------------------------------------------
      // Check Firebase Messaging Support
      // -------------------------------------------------
      const messagingSupported = await isSupported();

      if (!messagingSupported) {
        console.warn(
          "⚠️ Firebase Cloud Messaging is not supported in this browser."
        );

        toast.warning(
          "Firebase push notifications are not supported in this browser."
        );

        return;
      }

      console.log(
        "✅ Firebase Messaging is supported."
      );

      // -------------------------------------------------
      // Request Notification Permission
      // -------------------------------------------------
      let permission = Notification.permission;

      console.log(
        "🔔 Current notification permission:",
        permission
      );

      if (permission === "default") {
        permission =
          await Notification.requestPermission();
      }

      console.log(
        "🔔 Final notification permission:",
        permission
      );

      if (permission !== "granted") {
        console.warn(
          "⚠️ Notification permission was not granted."
        );

        toast.warning(
          "Please allow browser notifications to receive CRM alerts."
        );

        return;
      }

      // -------------------------------------------------
      // Register Firebase Messaging Service Worker
      // -------------------------------------------------
      console.log(
        "🔄 Registering Firebase Messaging Service Worker..."
      );

      const registration =
        await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
          {
            scope: "/",
          }
        );

      console.log(
        "✅ Firebase Messaging Service Worker registered:",
        registration.scope
      );

      // -------------------------------------------------
      // Wait for Service Worker
      // -------------------------------------------------
      const readyRegistration =
        await navigator.serviceWorker.ready;

      console.log(
        "✅ Firebase Service Worker ready:",
        readyRegistration.scope
      );

      // -------------------------------------------------
      // Initialize Firebase Messaging
      // -------------------------------------------------
      const messaging = getMessaging(app);

      console.log(
        "✅ Firebase Messaging initialized."
      );

      // -------------------------------------------------
      // Generate FCM Token
      // -------------------------------------------------
      console.log(
        "🔄 Requesting FCM token..."
      );

      const token = await getToken(messaging, {
        vapidKey: FCM_VAPID_KEY,
        serviceWorkerRegistration:
          readyRegistration,
      });

      // -------------------------------------------------
      // Validate Token
      // -------------------------------------------------
      if (!token) {
        console.warn(
          "⚠️ Firebase did not return an FCM token."
        );

        toast.warning(
          "Unable to generate notification token."
        );

        return;
      }

      console.log(
        "✅ FCM token generated successfully."
      );

      // -------------------------------------------------
      // Save FCM Token
      // -------------------------------------------------
      console.log(
        "🔄 Saving FCM token to admin-users..."
      );

      console.log(
        "🔎 Auth UID used for write:",
        userId
      );

      console.log(
        "🔎 Currently signed-in UID:",
        auth.currentUser?.uid
      );

      const adminRef = doc(
        db,
        "admin-users",
        userId
      );

      await setDoc(
        adminRef,
        {
          fcmToken: token,
          fcmTokenUpdatedAt:
            serverTimestamp(),
        },
        {
          merge: true,
        }
      );

      console.log(
        "✅ FCM token saved successfully to admin-users."
      );

      toast.success(
        "✅ Notifications enabled successfully!"
      );
    } catch (err) {
      console.error(
        "======================================"
      );

      console.error(
        "🔥 FCM SETUP FAILED"
      );

      console.error(
        "Error:",
        err
      );

      console.error(
        "Error Code:",
        err?.code
      );

      console.error(
        "Error Message:",
        err?.message
      );

      console.error(
        "======================================"
      );

      // -------------------------------------------------
      // Permission Errors
      // -------------------------------------------------
      if (
        err?.code ===
        "messaging/permission-blocked"
      ) {
        toast.error(
          "Browser notification permission is blocked. Please allow notifications."
        );

        return;
      }

      // -------------------------------------------------
      // Service Worker Errors
      // -------------------------------------------------
      if (
        err?.code ===
        "messaging/failed-service-worker-registration"
      ) {
        toast.error(
          "Firebase Messaging Service Worker registration failed."
        );

        return;
      }

      // -------------------------------------------------
      // Invalid VAPID Key
      // -------------------------------------------------
      if (
        err?.code ===
        "messaging/invalid-vapid-key"
      ) {
        toast.error(
          "Invalid Firebase Web Push certificate key."
        );

        return;
      }

      // -------------------------------------------------
      // Firestore Permission Error
      // -------------------------------------------------
      if (
        err?.code === "permission-denied"
      ) {
        toast.error(
          "Firestore permission denied while updating your admin profile."
        );

        console.error(
          "⚠️ Check Firestore Rules for admin-users/{userId}. " +
            "Confirm the document ID matches request.auth.uid, " +
            "and that read/create/update are allowed for that user."
        );

        return;
      }

      // -------------------------------------------------
      // Generic Error
      // -------------------------------------------------
      toast.error(
        `FCM setup failed: ${
          err?.message || "Unknown error"
        }`
      );
    }
  };

  // ===================================================
  // Foreground FCM Notifications
  // ===================================================
  useEffect(() => {
    let unsubscribe = null;

    const setupForegroundListener =
      async () => {
        try {
          // ------------------------------------------------
          // Browser Support
          // ------------------------------------------------
          if (
            typeof window ===
            "undefined"
          ) {
            return;
          }

          if (
            !("Notification" in window)
          ) {
            return;
          }

          if (
            !("serviceWorker" in navigator)
          ) {
            return;
          }

          // ------------------------------------------------
          // Check FCM Support
          // ------------------------------------------------
          const messagingSupported =
            await isSupported();

          if (!messagingSupported) {
            console.warn(
              "⚠️ Foreground FCM is not supported."
            );

            return;
          }

          // ------------------------------------------------
          // Initialize Messaging
          // ------------------------------------------------
          const messaging =
            getMessaging(app);

          // ------------------------------------------------
          // Listen for Foreground Messages
          // ------------------------------------------------
          unsubscribe = onMessage(
            messaging,
            (payload) => {
              console.log(
                "📩 Foreground FCM message:",
                payload
              );

              const title =
                payload?.notification
                  ?.title ||
                "UnlimitedGig Notification";

              const body =
                payload?.notification
                  ?.body || "";

              const bookingId =
                payload?.data?.bookingId;

              // ------------------------------------------------
              // Browser Notification
              // ------------------------------------------------
              if (
                Notification.permission ===
                "granted"
              ) {
                try {
                  const notification =
                    new Notification(
                      title,
                      {
                        body: body,
                        icon: "/logo192.png",
                        data: {
                          bookingId:
                            bookingId ||
                            null,
                        },
                      }
                    );

                  notification.onclick =
                    () => {
                      window.focus();

                      if (bookingId) {
                        window.location.href =
                          `/booking/${bookingId}`;
                      }

                      notification.close();
                    };
                } catch (
                  notificationError
                ) {
                  console.error(
                    "❌ Browser notification error:",
                    notificationError
                  );

                  toast.info(
                    `${title}: ${body}`
                  );
                }
              } else {
                // ------------------------------------------------
                // Toast Fallback
                // ------------------------------------------------
                toast.info(
                  `${title}: ${body}`
                );
              }
            }
          );

          console.log(
            "✅ Foreground FCM listener initialized."
          );
        } catch (err) {
          console.error(
            "❌ Foreground FCM Error:",
            err
          );
        }
      };

    setupForegroundListener();

    // ------------------------------------------------
    // Cleanup
    // ------------------------------------------------
    return () => {
      if (
        typeof unsubscribe ===
        "function"
      ) {
        unsubscribe();

        console.log(
          "🧹 Foreground FCM listener removed."
        );
      }
    };
  }, []);

  // ===================================================
  // Application UI
  // ===================================================
  return (
    <div className="container">
      <div className="card">
        {!currentUser ? (
          <Login
            onLoginSuccess={
              handleLoginSuccess
            }
          />
        ) : (
          <FirestoreData
            currentAdminUser={
              currentUser
            }
            setCurrentAdminUser={
              setCurrentUser
            }
          />
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
      />
    </div>
  );
}

// =====================================================
// Referral Code Generator
// =====================================================
export function generateReferralCode(
  displayName = "USER"
) {
  const base =
    displayName
      ?.split(" ")[0]
      ?.toUpperCase() || "USER";

  return (
    base +
    Math.floor(
      100 + Math.random() * 900
    )
  );
}

// =====================================================
// Export App
// =====================================================
export default App;
